// Connectors on the web (AB#200, the web half of AB#199): where a connector's ends are, the route
// the renderer draws, and what the editor's connector tool hit-tests. Pure: no DOM, no React, no
// Yjs — the renderer (render.ts, shared with the thumbnails and render_slide) draws from here, and
// connector-edits.ts writes what the editor decides.
//
// THE BINDINGS ARE THE TRUTH. A connector (a `line` or `elbow` shape) glued at an end is drawn from
// the site it is glued to, resolved at render time: a shape moved, resized or rotated — by this
// member, by an undo, or by another member — re-routes its connectors without anything being
// written to them. The stored box is only the fallback of an unbound end (its start is the box's
// top-left corner, its end the bottom-right one, turned by `rot`: lineEnds). The web reads a slide
// FLAT, so this is the backend's domain/slides.ts#connectorEnds / connectorRoute / unbindConnector
// over the flat snapshot; both resolve sites with the one shared evaluator
// (lib/slide-presets.ts#connectionSites / routeConnector), pinned by shared/slide-presets.fixture.json.
import {
  connectionSites,
  lineBox,
  lineEnds,
  routeConnector,
  type ConnectorEnd,
  type ConnectorPreset,
  type ConnectorRoute,
  type PlacedBox,
} from '../lib/slide-presets'
import { SLIDE_W, bindingHolds, isConnector, labelPosOf, round, sitePreset, type Binding, type ElementSnapshot, type ElementStyle, type LabelSide, type SlideSnapshot } from './model'

/** How close (slide px) the pointer must come to an element for a connector end to glue to it. */
export const SNAP_PX = 12

type Point = { x: number; y: number }

// ── frames: the groups an element sits in ───────────────────────────────────────────────────────

const FRAMES = new WeakMap<SlideSnapshot, Map<string, ElementSnapshot[]>>()

/** The groups each element sits in, outermost first (computed once per snapshot). */
function frames(s: SlideSnapshot): Map<string, ElementSnapshot[]> {
  const cached = FRAMES.get(s)
  if (cached) return cached
  const out = new Map<string, ElementSnapshot[]>()
  const visit = (ids: readonly string[], groups: ElementSnapshot[]): void => {
    for (const id of ids) {
      const e = s.elements[id]
      if (!e || out.has(id)) continue
      out.set(id, groups)
      if (e.children) visit(e.children, [...groups, e])
    }
  }
  visit(s.order, [])
  for (const id of Object.keys(s.elements)) if (!out.has(id)) out.set(id, [])
  FRAMES.set(s, out)
  return out
}

/** The groups `id` sits in, outermost first. */
export const groupsAround = (s: SlideSnapshot, id: string): ElementSnapshot[] => frames(s).get(id) ?? []

/** Turn a point (and its direction) by `deg` about the centre of `about`; numbers to 1/100. */
function turn(p: ConnectorEnd, about: { x: number; y: number; w: number; h: number }, deg: number): ConnectorEnd {
  if (!deg) return p
  const cx = about.x + about.w / 2
  const cy = about.y + about.h / 2
  const r = (deg * Math.PI) / 180
  const dx = p.x - cx
  const dy = p.y - cy
  const out: ConnectorEnd = { x: round(cx + dx * Math.cos(r) - dy * Math.sin(r)), y: round(cy + dx * Math.sin(r) + dy * Math.cos(r)) }
  if (p.ang !== undefined) out.ang = round((((p.ang + deg) % 360) + 360) % 360) % 360
  return out
}

/** A point of `id`'s frame (its groups' unrotated frame) on the slide. */
export function frameToSlide<P extends ConnectorEnd>(s: SlideSnapshot, id: string, p: P): ConnectorEnd {
  let q: ConnectorEnd = p
  for (const g of [...groupsAround(s, id)].reverse()) q = turn(q, g, g.rot)
  return q
}

/** A slide point in `id`'s frame. */
export function slideToFrame(s: SlideSnapshot, id: string, p: Point): Point {
  let q: ConnectorEnd = p
  for (const g of groupsAround(s, id)) q = turn(q, g, -g.rot)
  return { x: q.x, y: q.y }
}

// ── the ends and the route ──────────────────────────────────────────────────────────────────────

/** Where binding `b` of connector `conn` glues, in the connector's frame; null when it cannot hold. */
function resolveBinding(s: SlideSnapshot, conn: ElementSnapshot, b: Binding | undefined): ConnectorEnd | null {
  if (!b || !bindingHolds(conn.id, b, s.elements)) return null
  const t = s.elements[b.el] as ElementSnapshot
  const site = connectionSites(sitePreset(t), t)[b.site]
  if (!site) return null
  let p: ConnectorEnd = frameToSlide(s, t.id, site)
  for (const g of groupsAround(s, conn.id)) p = turn(p, g, -g.rot)
  return p
}

/**
 * A connector's two ends in its own frame (backend connectorEnds): a bound end is its site — which
 * turns with the element and with any rotated group around it — carrying the direction it leaves
 * the site; an unbound one is the stored point. `bound` tells which ends resolved.
 */
export function connectorEnds(s: SlideSnapshot, conn: ElementSnapshot): { start: ConnectorEnd; end: ConnectorEnd; bound: { from: boolean; to: boolean } } {
  const [a, b] = lineEnds(conn)
  const start = resolveBinding(s, conn, conn.from)
  const end = resolveBinding(s, conn, conn.to)
  return { start: start ?? a, end: end ?? b, bound: { from: start !== null, to: end !== null } }
}

/** The route of a connector bound at an end at least (backend connectorRoute); null otherwise. */
export function connectorRoute(s: SlideSnapshot, conn: ElementSnapshot): ConnectorRoute | null {
  if (!isConnector(conn)) return null
  const { start, end, bound } = connectorEnds(s, conn)
  if (!bound.from && !bound.to) return null
  return routeConnector(conn.geom as ConnectorPreset, start, end, conn.geom === 'elbow' ? conn.adj : undefined)
}

/**
 * What the renderer draws for a connector: the box its node takes (in its frame) and its polyline in
 * that box, start to end. Bound: the route, in the box of its points (unturned). Unbound: the preset
 * in its stored box — a line from the top-left corner to the bottom-right one, an elbow as
 * bentConnector3 (the middle at `adj`, half-way by default) — turned by `rot`, as the export writes it.
 */
export function connectorPath(s: SlideSnapshot, conn: ElementSnapshot): { box: PlacedBox; points: Array<[number, number]>; bound: boolean } {
  const route = connectorRoute(s, conn)
  if (route) {
    const xs = route.points.map((p) => p[0])
    const ys = route.points.map((p) => p[1])
    const x = Math.min(...xs)
    const y = Math.min(...ys)
    return {
      box: { x, y, w: round(Math.max(...xs) - x), h: round(Math.max(...ys) - y), rot: 0 },
      points: route.points.map(([px, py]) => [round(px - x), round(py - y)]),
      bound: true,
    }
  }
  const box = { x: conn.x, y: conn.y, w: conn.w, h: conn.h, rot: conn.rot }
  if (conn.geom !== 'elbow') return { box, points: [[0, 0], [conn.w, conn.h]], bound: false }
  const x1 = round((conn.w * (conn.adj ?? 50000)) / 100000)
  return { box, points: [[0, 0], [x1, 0], [x1, conn.h], [conn.w, conn.h]], bound: false }
}

/** A connector's ends on the slide (its own frame turned by its groups). */
export function endsOnSlide(s: SlideSnapshot, conn: ElementSnapshot): { start: ConnectorEnd; end: ConnectorEnd; bound: { from: boolean; to: boolean } } {
  const { start, end, bound } = connectorEnds(s, conn)
  return { start: frameToSlide(s, conn.id, start), end: frameToSlide(s, conn.id, end), bound }
}

/** An elbow's middle segment on the slide: its mid-point and whether it runs vertically (its handle). */
export function elbowMiddle(s: SlideSnapshot, conn: ElementSnapshot): { at: Point; vertical: boolean } | null {
  if (conn.geom !== 'elbow') return null
  const { box, points } = connectorPath(s, conn)
  const [p1, p2] = [points[1], points[2]]
  if (!p1 || !p2) return null
  // The polyline lives in the node's box, turned by `rot` about its centre (an unbound elbow).
  const local = (p: [number, number]): ConnectorEnd => turn({ x: box.x + p[0], y: box.y + p[1] }, box, box.rot)
  const a = frameToSlide(s, conn.id, local(p1))
  const b = frameToSlide(s, conn.id, local(p2))
  return { at: { x: round((a.x + b.x) / 2), y: round((a.y + b.y) / 2) }, vertical: Math.abs(b.x - a.x) < Math.abs(b.y - a.y) }
}

/**
 * The adjust value that puts an elbow's middle segment through slide point `p` (the handle dragged):
 * along the route's main axis, in 1/100000 of the way from start to end. Null when the ends are not
 * apart along that axis (the middle cannot move).
 */
export function adjustThrough(s: SlideSnapshot, conn: ElementSnapshot, p: Point): number | null {
  if (conn.geom !== 'elbow') return null
  const q = slideToFrame(s, conn.id, p)
  const { start, end, bound } = connectorEnds(s, conn)
  if (bound.from || bound.to) {
    // The main axis, as routeConnector picks it: the one the start's site faces along (a free
    // start: the end's), else the longer one.
    const sideways = (ang: number): boolean => Math.abs(Math.cos((ang * Math.PI) / 180)) >= Math.abs(Math.sin((ang * Math.PI) / 180)) - 1e-9
    const horizontal =
      start.ang !== undefined ? sideways(start.ang) : end.ang !== undefined ? sideways(end.ang) : Math.abs(end.x - start.x) >= Math.abs(end.y - start.y)
    const [a, b, v] = horizontal ? [start.x, end.x, q.x] : [start.y, end.y, q.y]
    return b !== a ? Math.round(((v - a) / (b - a)) * 100000) : null
  }
  // Unbound: the preset in its box, turned by rot — the main axis is the box's width.
  const c = { x: conn.x + conn.w / 2, y: conn.y + conn.h / 2 }
  const r = (-conn.rot * Math.PI) / 180
  const lx = c.x + (q.x - c.x) * Math.cos(r) - (q.y - c.y) * Math.sin(r)
  return conn.w !== 0 ? Math.round(((lx - conn.x) / conn.w) * 100000) : null
}

/**
 * A connector with the bindings `drop` selects removed, each such end FROZEN where it resolves now
 * (backend unbindConnector): the stored box re-derived from both ends, the other binding kept. Null
 * when nothing is dropped.
 */
export function unbindEnds(s: SlideSnapshot, conn: ElementSnapshot, drop: (b: Binding) => boolean): { box: PlacedBox; from?: Binding; to?: Binding } | null {
  const dropFrom = conn.from !== undefined && drop(conn.from)
  const dropTo = conn.to !== undefined && drop(conn.to)
  if (!dropFrom && !dropTo) return null
  const { start, end } = connectorEnds(s, conn)
  return {
    box: lineBox(start, end),
    ...(conn.from && !dropFrom ? { from: conn.from } : {}),
    ...(conn.to && !dropTo ? { to: conn.to } : {}),
  }
}

/** The connectors of a slide glued (at either end) to one of `ids`. */
export function connectorsOn(s: SlideSnapshot, ids: ReadonlySet<string>): ElementSnapshot[] {
  return Object.values(s.elements).filter((e) => isConnector(e) && ((e.from && ids.has(e.from.el)) || (e.to && ids.has(e.to.el))))
}

// ── sites and hit-testing (the connector tool) ──────────────────────────────────────────────────

/** Can a connector end glue to this element? Any element but a group or another connector. */
export const gluable = (e: ElementSnapshot): boolean => e.kind !== 'group' && !isConnector(e)

/** An element's connection sites on the slide, in PowerPoint's order (its index is the binding's `site`). */
export function sitesOnSlide(s: SlideSnapshot, id: string): ConnectorEnd[] {
  const e = s.elements[id]
  if (!e) return []
  return connectionSites(sitePreset(e), e).map((p) => frameToSlide(s, id, p))
}

/** Is slide point `p` on element `id` (its turned box, grown by `pad`)? */
export function contains(s: SlideSnapshot, id: string, p: Point, pad = 0): boolean {
  const e = s.elements[id]
  if (!e) return false
  const q = slideToFrame(s, id, p)
  const c = { x: e.x + e.w / 2, y: e.y + e.h / 2 }
  const r = (-e.rot * Math.PI) / 180
  const lx = c.x + (q.x - c.x) * Math.cos(r) - (q.y - c.y) * Math.sin(r)
  const ly = c.y + (q.x - c.x) * Math.sin(r) + (q.y - c.y) * Math.cos(r)
  return lx >= e.x - pad && lx <= e.x + e.w + pad && ly >= e.y - pad && ly <= e.y + e.h + pad
}

/** Every element id in paint order (back to front), group members in their group's place. */
export function paintOrder(s: SlideSnapshot): string[] {
  const out: string[] = []
  const visit = (ids: readonly string[]): void => {
    for (const id of ids) {
      const e = s.elements[id]
      if (!e || out.includes(id)) continue
      out.push(id)
      if (e.children) visit(e.children)
    }
  }
  visit(s.order)
  return out
}

/**
 * The index of the site of `sites` nearest to `p`. Sites sharing that point (a box's corner carries
 * one per side, AB#325) go to the one whose side `p` lies farther outside of: how far `p` is past
 * the site along the direction it faces.
 */
function nearest(sites: readonly ConnectorEnd[], p: Point): number {
  let best = -1
  let dist = Infinity
  let out = -Infinity
  sites.forEach((q, i) => {
    const d = Math.hypot(q.x - p.x, q.y - p.y)
    const r = ((q.ang ?? 0) * Math.PI) / 180
    const o = (p.x - q.x) * Math.cos(r) + (p.y - q.y) * Math.sin(r)
    const tie = best >= 0 && Math.abs(d - dist) < 1e-6 && q.x === sites[best]?.x && q.y === sites[best]?.y
    if (tie ? o > out + 1e-9 : d < dist) {
      dist = d
      best = i
      out = o
    }
  })
  return best
}

/** A site a connector end can glue to: the binding, and where it is on the slide. */
export interface SiteHit extends Binding {
  x: number
  y: number
}

/**
 * Where a connector end dropped at slide point `p` glues: the topmost element under the pointer
 * (within SNAP_PX of its box; not a group, not a connector, not `exclude`) and its nearest site. Null
 * on empty canvas — that end stays free.
 */
export function siteAt(s: SlideSnapshot, p: Point, exclude?: string, pad = SNAP_PX): SiteHit | null {
  const order = paintOrder(s)
  for (let i = order.length - 1; i >= 0; i--) {
    const id = order[i] as string
    const e = s.elements[id] as ElementSnapshot
    if (id === exclude || !gluable(e) || !contains(s, id, p, pad)) continue
    const sites = sitesOnSlide(s, id)
    const k = nearest(sites, p)
    if (k < 0) continue
    const q = sites[k] as ConnectorEnd
    return { el: id, site: k, x: q.x, y: q.y }
  }
  return null
}

/**
 * The keyboard's Connect: the facing sites of two elements — the pair (one site of each) closest to
 * each other. Null when either has no site.
 */
export function facingSites(s: SlideSnapshot, a: string, b: string): { from: SiteHit; to: SiteHit } | null {
  const sa = sitesOnSlide(s, a)
  const sb = sitesOnSlide(s, b)
  let best: { from: SiteHit; to: SiteHit } | null = null
  let dist = Infinity
  sa.forEach((p, i) => {
    sb.forEach((q, j) => {
      const d = Math.hypot(q.x - p.x, q.y - p.y)
      if (d < dist - 1e-9) {
        dist = d
        best = { from: { el: a, site: i, x: p.x, y: p.y }, to: { el: b, site: j, x: q.x, y: q.y } }
      }
    })
  })
  return best
}

// ── live previews: a gesture under way, before it is written ────────────────────────────────────

/** An element's box (and rotation) as a gesture under way shows it. */
export interface Moved {
  id: string
  x: number
  y: number
  w: number
  h: number
  rot: number
}

/**
 * The slide as a drag, resize or rotation under way shows it — the moved elements in their new
 * boxes, a group's members moved (or scaled) with it — so the connectors glued to them can be drawn
 * re-routed before anything is written.
 */
export function withMoved(s: SlideSnapshot, moved: readonly Moved[]): SlideSnapshot {
  const elements = { ...s.elements }
  for (const m of moved) {
    const e = s.elements[m.id]
    if (!e) continue
    elements[m.id] = { ...e, x: m.x, y: m.y, w: m.w, h: m.h, rot: m.rot }
    const sx = e.w ? m.w / e.w : 1
    const sy = e.h ? m.h / e.h : 1
    const walk = (ids: readonly string[] | undefined): void => {
      for (const c of ids ?? []) {
        const k = s.elements[c]
        if (!k || c === m.id) continue
        elements[c] = { ...k, x: m.x + (k.x - e.x) * sx, y: m.y + (k.y - e.y) * sy, w: k.w * sx, h: k.h * sy }
        walk(k.children)
      }
    }
    walk(e.children)
  }
  return { ...s, elements }
}

/** Every element a gesture on `ids` moves: them and what is nested in them. */
export function movedIds(s: SlideSnapshot, ids: readonly string[]): Set<string> {
  const out = new Set<string>()
  const walk = (id: string): void => {
    if (out.has(id)) return
    out.add(id)
    for (const c of s.elements[id]?.children ?? []) walk(c)
  }
  ids.forEach(walk)
  return out
}

/** A connector's polyline on the slide, start to end (what the connector tool previews). */
export function pathOnSlide(s: SlideSnapshot, conn: ElementSnapshot): Point[] {
  const { box, points } = connectorPath(s, conn)
  return points.map(([x, y]) => {
    const p = frameToSlide(s, conn.id, turn({ x: box.x + x, y: box.y + y }, box, box.rot))
    return { x: p.x, y: p.y }
  })
}

/**
 * Connector `conn` with one end glued to a site, or freed at a slide point (`at`), the other end as it
 * is — what a drag of an end handle writes (connector-edits.ts#setConnectorEnd) and previews.
 */
export function withEnd(s: SlideSnapshot, conn: ElementSnapshot, which: 'from' | 'to', at: SiteHit | Point): ElementSnapshot {
  const { start, end } = connectorEnds(s, conn)
  const p = slideToFrame(s, conn.id, at)
  const next: ElementSnapshot = { ...conn, ...(which === 'from' ? lineBox(p, end) : lineBox(start, p)) }
  if ('el' in at) next[which] = { el: at.el, site: at.site }
  else delete next[which]
  return next
}

// ── the label (AB#324) — backend domain/slides.ts, kept in parity ───────────────────────────────
//
// A connector's label is its own text (empty = no label), drawn HORIZONTAL at a point of its route:
// `labelPos` % of the routed polyline's length from the start (bends included), above, on or below
// the segment that point lies on. Bound ends resolve at render time, so the label follows the
// connector when a glued shape moves.

/** The gap (px) between the line's edge and an above / below label's box. */
export const LABEL_GAP_PX = 4
/** The widest a label wraps at: 40 % of the slide. */
export const LABEL_MAX_W = SLIDE_W * 0.4
/** The slide-coloured pad (vertical, horizontal px) behind an `on` label, masking the line. */
export const LABEL_ON_PAD: readonly [number, number] = [2, 4]

/** A point of a route and the unit direction (start → end) of the segment it lies on. */
export interface RoutePoint {
  x: number
  y: number
  dx: number
  dy: number
}

/**
 * The point at `pct` % of a polyline's arc length, and its segment's direction. At a bend, the
 * segment that ends there; zero-length segments are skipped (a degenerate polyline: its first
 * point, heading right).
 */
export function pointAlong(points: ReadonlyArray<readonly [number, number]>, pct: number): RoutePoint {
  const segs: Array<{ a: readonly [number, number]; b: readonly [number, number]; len: number }> = []
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1] as readonly [number, number]
    const b = points[i] as readonly [number, number]
    const len = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (len > 0) segs.push({ a, b, len })
  }
  const first = points[0] ?? [0, 0]
  if (segs.length === 0) return { x: first[0], y: first[1], dx: 1, dy: 0 }
  const total = segs.reduce((n, g) => n + g.len, 0)
  const target = (total * Math.min(Math.max(pct, 0), 100)) / 100
  // The segment the target falls on (the last one, past rounding), and the length before it.
  let acc = 0
  let k = 0
  while (k < segs.length - 1 && target > acc + (segs[k] as { len: number }).len + 1e-9) acc += (segs[k++] as { len: number }).len
  const seg = segs[k] as { a: readonly [number, number]; b: readonly [number, number]; len: number }
  const t = Math.min(Math.max((target - acc) / seg.len, 0), 1)
  return {
    x: round(seg.a[0] + (seg.b[0] - seg.a[0]) * t),
    y: round(seg.a[1] + (seg.b[1] - seg.a[1]) * t),
    dx: (seg.b[0] - seg.a[0]) / seg.len,
    dy: (seg.b[1] - seg.a[1]) / seg.len,
  }
}

/**
 * The unit normal a label sits along for a segment's direction: `above` is the one pointing
 * screen-up (on an exactly vertical segment, screen-left), `below` its opposite, `on` none (0, 0).
 */
export function labelNormal(dir: { dx: number; dy: number }, side: LabelSide): Point {
  if (side === 'on') return { x: 0, y: 0 }
  const a = { x: dir.dy, y: -dir.dx }
  const up = Math.abs(a.y) > 1e-9 ? a.y < 0 : a.x < 0
  const k = (up ? 1 : -1) * (side === 'above' ? 1 : -1)
  return { x: k * a.x + 0, y: k * a.y + 0 }
}

/**
 * Where a label's box goes for an anchor: the point (`x`, `y`) its box touches and which fraction of
 * the box sits before it (`fx`, `fy`: left = x − fx·w, top = y − fy·h). `on` centres the box on the
 * anchor; above / below put the box's nearest edge (or corner, on a slanted segment) at
 * lineWidth / 2 + LABEL_GAP_PX from the line, along the normal.
 */
export function labelPlacement(at: RoutePoint, side: LabelSide, lineWidth: number): { x: number; y: number; fx: number; fy: number } {
  const n = labelNormal(at, side)
  const m = Math.max(Math.abs(n.x), Math.abs(n.y))
  if (m === 0) return { x: at.x, y: at.y, fx: 0.5, fy: 0.5 }
  const gap = lineWidth / 2 + LABEL_GAP_PX
  const f = (v: number): number => Math.round(((1 - v / m) / 2) * 10000) / 10000 + 0
  return { x: round(at.x + n.x * gap), y: round(at.y + n.y * gap), fx: f(n.x), fy: f(n.y) }
}

/** The line width a connector is drawn with (0 with no line), which the label's gap clears. */
export const connectorLineWidth = (style: ElementStyle): number => (style.line && style.line !== 'none' ? (style.lineWidth ?? 1) : 0)

/**
 * Where a connector's label is anchored on the slide: `labelPos` % of the arc length of its routed
 * polyline (connectorPath), glued or free, straight or elbow, and the direction of that segment.
 */
export function labelAnchor(s: SlideSnapshot, conn: ElementSnapshot): RoutePoint {
  return pointAlong(pathOnSlide(s, conn).map((p) => [p.x, p.y] as [number, number]), labelPosOf(conn.style))
}

/** The total rotation of a connector's node on the slide (its own, its groups'), which a horizontal label undoes. */
export function nodeRotation(s: SlideSnapshot, conn: ElementSnapshot): number {
  const own = connectorPath(s, conn).box.rot
  return groupsAround(s, conn.id).reduce((r, g) => r + g.rot, own)
}
